import React, {useCallback, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {Menu, MenuItem, Pagination, Tooltip} from '@mui/material';
import SearchInput from '../../../components/common/SearchInput';
import PageSizeSelector from '../../../components/common/PageSizeSelector';
import {ReportedIssue, ReportedIssueStatus} from '../../../models/reportedIssues.models';
import {useSnackbar} from '../../../contexts/SnackbarContext';
import {ReactComponent as ChevronUpIcon} from '../../../assets/icons/chevron-up.svg';
import {ReactComponent as TrashIcon} from '../../../assets/icons/trash.svg';
import {ReactComponent as AttachmentIcon} from '../../../assets/icons/attachment.svg';
import {ReactComponent as MoreIcon} from '../../../assets/icons/more.svg';
import {
    ActionButton,
    ContextMenu,
    ContextMenuItem,
    OptionsButton
} from "../../../components/data-sources/DataSourceTable.styled";
import {Header, StatusCell,} from './ReportedIssuesList.styled';
import {useTranslation} from 'react-i18next';
import {parseError} from "../../../errors/http.service";
import ResponsiveTable, {ResponsiveTableColumn} from "../../../components/common/ResponsiveTable";
import {
    ControlsRow,
    PageSizeWrapper,
    PaginationWrapper,
    SearchWrapper,
    StyledTableContainer
} from "../../../components/common/TableContainer.styled";

const SEARCH_DEBOUNCE_MS = 400;

const ReportedIssuesList = () => {
    const [issues, setIssues] = useState<ReportedIssue[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedIssueId, setSelectedIssueId] = useState<number | null>(null);
    const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
    const [attachmentsMenu, setAttachmentsMenu] = useState<null | { mouseX?: number; mouseY?: number; anchorEl?: HTMLElement | null }>(null);
    const [selectedAttachmentsIssueId, setSelectedAttachmentsIssueId] = useState<number | null>(null);

    const [contextMenu, setContextMenu] = useState<{
        mouseX?: number;
        mouseY?: number;
        anchorEl?: HTMLElement | null;
        issue: ReportedIssue;
    } | null>(null);

    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    useEffect(() => {
        const normalizedSearchTerm = searchTerm.trim();
        const normalizedDebouncedSearchTerm = debouncedSearchTerm.trim();

        if (normalizedSearchTerm === normalizedDebouncedSearchTerm) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [debouncedSearchTerm, searchTerm]);

    const statusTranslations: Record<ReportedIssueStatus, string> = {
        [ReportedIssueStatus.TO_SOLVE]: t('reportedIssuesList.status.toSolve'),
        [ReportedIssueStatus.IN_PROGRESS]: t('reportedIssuesList.status.inProgress'),
        [ReportedIssueStatus.RESOLVED]: t('reportedIssuesList.status.resolved'),
    };

    const fetchIssues = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', (currentPage - 1).toString());
            params.append('size', pageSize.toString());
            const normalizedSearchTerm = debouncedSearchTerm.trim();
            if (normalizedSearchTerm) {
                params.append('title', normalizedSearchTerm);
            }

            const response = await axios.get('/contact-submissions', {params});
            setIssues(response.data.content);
            setTotalElements(response.data.totalElements || 0);
        } catch (error) {
            console.error('Error fetching issues:', error);
            showSnackbar(t('reportedIssuesList.fetchError'), 'error');
            setIssues([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearchTerm, pageSize, showSnackbar, t]);

    useEffect(() => {
        fetchIssues();
    }, [fetchIssues]);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const handlePageSizeChange = (value: number) => {
        setPageSize(value);
        setCurrentPage(1);
    };

    const handleStatusIconClick = (event: React.MouseEvent<HTMLElement>, issueId: number) => {
        setAnchorEl(event.currentTarget);
        setSelectedIssueId(issueId);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedIssueId(null);
    };

    const handleChangeStatus = async (newStatus: ReportedIssueStatus) => {
        if (selectedIssueId !== null) {
            try {
                await axios.patch(`/contact-submissions/${selectedIssueId}/status`, {contactSubmissionStatus: newStatus});
                showSnackbar(t('reportedIssuesList.statusChanged'), 'info');
                fetchIssues();
            } catch (error) {
                showSnackbar(t('reportedIssuesList.statusChangeError'), 'error');
            }
        }
        handleCloseMenu();
    };

    const handleToggleExpand = (issueId: number) => {
        setExpandedRows(prev => ({
            ...prev,
            [issueId]: !prev[issueId]
        }));
    };

    const handleDeleteIssue = async (issueId: number) => {
        try {
            await axios.delete(`/contact-submissions/${issueId}`);
            showSnackbar(t('reportedIssuesList.deleteSuccess'), 'success');
            fetchIssues();
        } catch (error) {
            showSnackbar(t('reportedIssuesList.deleteError'), 'error');
        }
    };

    const formatReportedDate = (dateString: string) => {
        const date = new Date(dateString);
        date.setHours(date.getHours() + 2);
        return date.toLocaleString(undefined, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleCloseAttachmentsMenu = () => {
        setAttachmentsMenu(null);
        setSelectedAttachmentsIssueId(null);
    };

    const handleContextMenu = useCallback((event: React.MouseEvent, issue: ReportedIssue) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            anchorEl: event.currentTarget as HTMLElement,
            issue,
        });
    }, []);

    const handleCloseContextMenu = useCallback(() => {
        setContextMenu(null);
    }, []);

    const handleMenuToggleExpand = useCallback(() => {
        if (!contextMenu) return;
        handleToggleExpand(contextMenu.issue.id);
        setContextMenu(null);
    }, [contextMenu]);

    const handleMenuOpenAttachments = useCallback(() => {
        if (!contextMenu) return;
        setAttachmentsMenu({
            anchorEl: contextMenu.anchorEl ?? null,
            mouseX: contextMenu.mouseX,
            mouseY: contextMenu.mouseY,
        });
        setSelectedAttachmentsIssueId(contextMenu.issue.id);
        setContextMenu(null);
    }, [contextMenu]);

    const handleMenuDelete = useCallback(() => {
        if (!contextMenu) return;
        handleDeleteIssue(contextMenu.issue.id);
        setContextMenu(null);
    }, [contextMenu]);

    const downloadAttachment = async (submissionId: number, attachmentId: number, fileName: string) => {
        try {
            const response = await axios.get(`/contact-submissions/${submissionId}/attachment/${attachmentId}`, {responseType: 'blob'});
            const blob = new Blob([response.data]);
            const link = document.createElement('a');
            link.href = globalThis.URL.createObjectURL(blob);
            link.download = fileName || 'downloaded-file';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            globalThis.URL.revokeObjectURL(link.href);
        } catch (error) {
            showSnackbar(parseError(error), "error");
        } finally {
            handleCloseAttachmentsMenu();
        }
    };

    const selectedIssueAttachments =
        issues.find(i => i.id === selectedAttachmentsIssueId)?.attachments || [];

    const columns: ResponsiveTableColumn<ReportedIssue>[] = useMemo(() => [
        {
            id: 'email',
            header: t('reportedIssuesList.emailHeader'),
            render: (i) => i.email,
        },
        {
            id: 'title',
            header: t('reportedIssuesList.titleHeader'),
            render: (i) => i?.title || t('reportedIssuesList.noTitle'),
        },
        {
            id: 'content',
            header: t('reportedIssuesList.contentHeader'),
            render: (i) => (
                <div>
                    {expandedRows[i.id] ? (
                        <div style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>{i.content}</div>
                    ) : (
                        <div style={{maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                            {i.content}
                        </div>
                    )}
                </div>
            ),
        },
        {
            id: 'reportedAt',
            header: t('reportedIssuesList.reportedDateHeader'),
            render: (i) => formatReportedDate(i.createdAt),
        },
        {
            id: 'status',
            header: t('reportedIssuesList.statusHeader'),
            render: (i) => (
                <StatusCell 
                    status={i.status} 
                    onClick={(e) => handleStatusIconClick(e, i.id)}
                    role="button"
                    aria-label={t('reportedIssuesList.status.ariaLabel') as string}
                >
                    {statusTranslations[i.status || ReportedIssueStatus.TO_SOLVE]}
                </StatusCell>
            ),
        },
        {
            id: 'actions',
            header: '',
            headerLabel: t('common.actions') as string,
            cellClassName: 'actions-cell',
            render: (i) => (
                <>
                    <Tooltip title={t('common.actions')}>
                        <ActionButton
                            className="mobile-only"
                            variant="outlined"
                            aria-haspopup="menu"
                            aria-controls={contextMenu?.issue.id === i.id ? `reported-issues-menu-${i.id}` : undefined}
                            aria-expanded={contextMenu?.issue.id === i.id ? true : undefined}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, i);
                            }}
                        >
                            {t('common.actions')}
                        </ActionButton>
                    </Tooltip>
                    <Tooltip title={t('common.actions')}>
                        <OptionsButton
                            className="desktop-only"
                            size="small"
                            aria-label={t('common.actions') as string}
                            aria-haspopup="menu"
                            aria-controls={contextMenu?.issue.id === i.id ? `reported-issues-menu-${i.id}` : undefined}
                            aria-expanded={contextMenu?.issue.id === i.id ? true : undefined}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, i);
                            }}
                        >
                            <MoreIcon aria-hidden={true} focusable={false}/>
                        </OptionsButton>
                    </Tooltip>
                </>
            ),
        },
    ], [t, statusTranslations, expandedRows, handleContextMenu, contextMenu?.issue.id]);

    return (
        <StyledTableContainer role="region" aria-labelledby="reported-issues-title">
                <Header>
                    <h2 id="reported-issues-title">{t('reportedIssuesList.title')}</h2>
                </Header>
                <ControlsRow>
                    <SearchWrapper>
                        <SearchInput
                            onChange={handleSearchChange}
                            value={searchTerm}
                            placeholder={t('reportedIssuesList.searchPlaceholder') || ''}
                            ariaLabel={t('searchInput.ariaLabel') as string}
                        />
                    </SearchWrapper>
                    <PageSizeWrapper>
                        <PageSizeSelector
                            selectedSize={pageSize}
                            onChange={handlePageSizeChange}
                            options={[10, 20, 50]}
                            ariaLabelGroup={t('pageSizeSelector.aria.group') as string}
                            getSizeAriaLabel={(size) => t('pageSizeSelector.aria.setSize', { size }) as string}
                        />
                    </PageSizeWrapper>
                </ControlsRow>

                <div aria-live="polite" aria-atomic={true} aria-busy={loading}>
                    {loading && (
                        <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                            {t('reportedIssuesList.loading')}
                        </div>
                    )}
                    <ResponsiveTable
                        columns={columns}
                        data={issues}
                        getRowKey={(i) => i.id}
                        emptyState={loading ? t('reportedIssuesList.loading') : t('reportedIssuesList.noIssues')}
                        ariaLabel={t('reportedIssuesList.table.ariaLabel') as string}
                    />
                </div>

                <ContextMenu
                    id={contextMenu ? `reported-issues-menu-${contextMenu.issue.id}` : undefined}
                    open={contextMenu !== null}
                    onClose={handleCloseContextMenu}
                    anchorEl={contextMenu?.anchorEl ?? null}
                    anchorReference={contextMenu?.anchorEl ? 'anchorEl' : 'anchorPosition'}
                    anchorPosition={
                        contextMenu && contextMenu.mouseY !== undefined && contextMenu.mouseX !== undefined
                            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                            : undefined
                    }
                    MenuListProps={{
                        autoFocusItem: true,
                        'aria-labelledby': contextMenu ? `reported-issues-menu-${contextMenu.issue.id}` : undefined,
                    }}
                >
                    {contextMenu && [
                        (contextMenu.issue.content && contextMenu.issue.content.length > 100 ? (
                            <ContextMenuItem key="toggle-expand" onClick={handleMenuToggleExpand}>
                                <ChevronUpIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px', transform: expandedRows[contextMenu.issue.id] ? 'none' : 'rotate(180deg)'}}/>
                                {expandedRows[contextMenu.issue.id] ? t('reportedIssuesList.collapse') : t('reportedIssuesList.expand')}
                            </ContextMenuItem>
                        ) : null),
                        (contextMenu.issue.attachments && contextMenu.issue.attachments.length ? (
                            <ContextMenuItem key="attachments" onClick={handleMenuOpenAttachments}>
                                <AttachmentIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                                {t('reportedIssuesList.attachments')}
                            </ContextMenuItem>
                        ) : null),
                        (
                            <ContextMenuItem key="delete" onClick={handleMenuDelete} style={{color: '#f44336'}}>
                                <TrashIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                                {t('reportedIssuesList.delete')}
                            </ContextMenuItem>
                        ),
                    ]}
                </ContextMenu>

                <Menu
                    id={selectedAttachmentsIssueId ? `reported-issues-attachments-${selectedAttachmentsIssueId}` : undefined}
                    anchorEl={attachmentsMenu?.anchorEl ?? null}
                    anchorReference={attachmentsMenu?.anchorEl ? 'anchorEl' : 'anchorPosition'}
                    anchorPosition={attachmentsMenu && attachmentsMenu.mouseY !== undefined && attachmentsMenu.mouseX !== undefined ? { top: attachmentsMenu.mouseY, left: attachmentsMenu.mouseX } : undefined}
                    open={Boolean(attachmentsMenu)}
                    onClose={handleCloseAttachmentsMenu}
                >
                    {selectedIssueAttachments.map(att => (
                        <MenuItem
                            key={att.id}
                            onClick={() =>
                                downloadAttachment(
                                    selectedAttachmentsIssueId as number,
                                    att.id,
                                    att.fileName
                                )
                            }
                        >
                            {att.fileName}
                        </MenuItem>
                    ))}
                </Menu>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                >
                    {Object.values(ReportedIssueStatus).map((statusValue) => (
                        <MenuItem key={statusValue} onClick={() => handleChangeStatus(statusValue)}>
                            {statusTranslations[statusValue]}
                        </MenuItem>
                    ))}
                </Menu>

                {totalElements > 0 && (
                    <PaginationWrapper>
                        <Pagination
                            count={Math.ceil(totalElements / pageSize)}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            aria-label={t('reportedIssuesList.pagination.ariaLabel') as string}
                        />
                    </PaginationWrapper>
                )}
        </StyledTableContainer>
    );
};

export default ReportedIssuesList;
